import {
    useCallback,
    useEffect,
    useRef,
    useState,
} from "react";
import Avatar from "../assets/svg/avatar.svg";
import daburLogo from "../assets/svg/appIcon.svg";
import InfiniteScroll from "react-infinite-scroll-component";
import Markdown from "markdown-to-jsx";
import classNames from "classnames";
import {
    getContentHistory,
    setPage,
    getDocumentSessionId,
    marketingAgentRequest,
    marketingAgentStatusCheck,
    setProjectQueryMessage,
    setProjectChatsessionId,
} from "../lib/redux/actions";
import TextInput from "../components/ui/TextInput";
import { useDispatch, useSelector } from "react-redux";
import SkeletonLoader from "../lib/common/components/loader/SkeletonLoader";
import { CustomLink, preprocessText } from "../lib/utils/helper";
import FileCard from "../components/chat/FileCard";
import { Mic, ChevronDown, Folder } from "lucide-react";
import { useLocation, useOutletContext, useParams } from "react-router-dom";
import CopyToClipboard from "../lib/common/components/CopyToClipboard";
import { deepSearchPreprocessText } from "../lib/utils/deep-search-helper";
import DeepSearchBotMarkdown from "../lib/common/components/markdown/DeepSearchBotMarkdown";
import PropTypes from "prop-types";
import { trackEvent, trackException } from "../lib/utils/telemetryService";
import { MESSAGES } from "../config/messages";
import { ProjectTogglesRow } from "../components/project/ProjectTogglesRow";
import RotatingTextLoader from "../lib/common/components/loader/RotatingTextLoader";

export default function GenerateMarketing() {

    const { isMobile, sidebarOpen, marketingChats, setMarketingChats } = useOutletContext()
    const location = useLocation();
    const { projectId, name } = useParams();
    const textRef = useRef("");
    const scrollRef = useRef(null);
    const isFetchingRef = useRef(false);

    const dispatch = useDispatch();
    const { selectedProjectChat, page, projectSessionId, projectQueryMessage } = useSelector(
        (state) => state.chat
    );

    const textDelayTimeout = useRef(null);
    const [displayedText, setDisplayedText] = useState("");
    const [isContentRendering, setIsContentRendering] = useState(false);
    const [tempId, setTempId] = useState(null);
    const inputRef = useRef(null);
    const [inputFocused, setInputFocused] = useState(false);
    const [inputText, setInputText] = useState("");
    const [hasMore, setHasMore] = useState(false);
    const [loading, setLoading] = useState(false);
    const [hasDocument, setHasDocument] = useState(false);

    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const scrollHeightRef = useRef(0);
    const scrollTimeoutRef = useRef(null);
    const [isLoadingHistory, setIsLoadingHistory] = useState(true);

    useEffect(() => {
        // When project changes, reset chats and pagination and refetch history
        setMarketingChats([]);
        dispatch(setPage(1));
        handleGetContentHistory(1);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId]);

    const handleGetContentHistory = async (pageNumber = 1) => {
        if (isFetchingRef.current) return;

        try {
            isFetchingRef.current = true;
            setIsLoadingMore(true);
            if (pageNumber === 1) setIsLoadingHistory(true);

            // Store current scroll height before loading new data
            if (scrollRef.current) {
                scrollHeightRef.current = scrollRef.current.scrollHeight;
            }

            const data = {
                page: pageNumber,
                limit: 10,
                chatType: 9, // AUDIO type
                projectId: projectId,
            };

            const res = await getContentHistory(data);
            const newChats = res.data.data || [];

            // Filter out duplicate entries (where questionId is null)
            const filteredChats = newChats.filter((chat) => chat.questionId !== null);

            const existingIds = new Set(marketingChats.map((chat) => chat._id));
            const uniqueNewChats = filteredChats.filter(
                (chat) => !existingIds.has(chat._id)
            );

            const processedMessages = uniqueNewChats.flatMap((chat) => {
                let parsedContent = {};

                try {
                    parsedContent =
                        typeof chat.content === "string" &&
                            chat.content.trim().startsWith("{")
                            ? JSON.parse(chat.content)
                            : {};
                } catch (error) {
                    console.log("JSON parse failed:", error);
                    parsedContent = {};
                }

                return [
                    {
                        role: "user",
                        message: chat.questionId.message,
                        _id: chat.questionId._id,
                        type: "text",
                    },
                    {
                        role: "assistant",
                        message: parsedContent?.content || "No response received.",
                        sources: parsedContent?.sources,
                        _id: `bot-${Date.now()}`,
                        projectSessionId: chat?.projectSessionId,
                    },
                ];
            });

            setMarketingChats((prev) => [...processedMessages, ...prev]);
            setHasMore(processedMessages.length > 0);

            // After state update, adjust scroll position
            // setTimeout(() => {
            //     if (scrollRef.current) {
            //         const newScrollHeight = scrollRef.current.scrollHeight;
            //         const prevScrollTop = scrollRef.current.scrollTop;
            //         const scrollDiff = newScrollHeight - scrollHeightRef.current;
            //         scrollRef.current.scrollTop = prevScrollTop + scrollDiff;
            //     }
            // }, 0);
        } catch (error) {
            console.error(error);
            trackException(error);
        } finally {
            isFetchingRef.current = false;
            setIsLoadingMore(false);
            if (pageNumber === 1) setIsLoadingHistory(false);
        }
    };

    const fetchMoreData = async () => {
        if (isFetchingRef.current || !hasMore || isLoadingMore) return;

        // Only fetch more if we're near the top
        if (scrollRef.current && scrollRef.current.scrollTop > 100) return;

        // Clear any existing timeout
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        // Set a new timeout
        scrollTimeoutRef.current = setTimeout(async () => {
            const nextPage = page + 1;
            await handleGetContentHistory(nextPage);
            dispatch(setPage(nextPage));
        }, 300); // 300ms debounce
    };

    const handleTextDelay = useCallback((msg) => {
        // Clear any existing timeouts
        if (textDelayTimeout.current) {
            clearTimeout(textDelayTimeout.current);
        }

        setIsContentRendering(true);
        let currentIndex = 0;
        const typingSpeed = 2;
        textRef.current = ""; // Use ref instead of state for intermediate values

        const typeNextCharacter = () => {
            if (currentIndex < msg.length) {
                textRef.current = msg.substring(0, currentIndex + 1);
                setDisplayedText(textRef.current);
                currentIndex += 1;
                textDelayTimeout.current = setTimeout(typeNextCharacter, typingSpeed);
            } else {
                setIsContentRendering(false);
            }
        };

        typeNextCharacter();

        // Cleanup function
        return () => {
            if (textDelayTimeout.current) {
                clearTimeout(textDelayTimeout.current);
            }
            setIsContentRendering(false);
        };
    }, []);



    const getDoc = async (id) => {
        const res = await getDocumentSessionId(id);

        setHasDocument(res?.data?.document?.length > 0);
    };

    useEffect(() => {
        if (selectedProjectChat?._id) {
            getDoc(selectedProjectChat?._id);
        } else {
            setHasDocument(false);
        }
    }, [selectedProjectChat?._id]);

    useEffect(() => {
        if (
            loading &&
            projectQueryMessage &&
            projectSessionId &&
            projectSessionId === selectedProjectChat?._id
        ) {
            setMarketingChats((prev) => [...prev, projectQueryMessage]);
        }
        // eslint-disable-next-line
    }, [selectedProjectChat]);

    useEffect(() => {
        if (
            location.pathname !== `project/${name}/marketing/${projectId}` &&
            selectedProjectChat?._id &&
            (marketingChats?.length === 0 ||
                (marketingChats[marketingChats.length - 1]?._id &&
                    !marketingChats[marketingChats.length - 1]._id.includes("temp-user-")))
        ) {
            // Clear existing marketingChats when selecting a new chat
            setMarketingChats([]);
            setHasDocument(false);
            handleGetContentHistory(1);
        }
        // eslint-disable-next-line
    }, [selectedProjectChat]);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.scrollLeft = inputRef.current.scrollWidth;
        }
    }, [inputText]);

    useEffect(() => {
        if (marketingChats.length > 0) {
            const lastChat = marketingChats[marketingChats.length - 1];
            if (lastChat.role === "assistant" && lastChat.latest_message) {
                handleTextDelay(lastChat.message);
            } else {
                // Immediately show full message if not latest assistant message
                setDisplayedText(lastChat.message);
            }
        }
    }, [marketingChats, handleTextDelay]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden && textDelayTimeout.current) {
                clearTimeout(textDelayTimeout.current);
                setIsContentRendering(false);
            } else if (!document.hidden && marketingChats.length > 0) {
                // When returning to the tab, immediately show the full text
                const lastChat = marketingChats[marketingChats.length - 1];
                if (lastChat.role === "assistant" && lastChat.latest_message) {
                    setDisplayedText(lastChat.message);
                    setIsContentRendering(false);
                }
            }
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        return () => {
            if (textDelayTimeout.current) {
                clearTimeout(textDelayTimeout.current);
            }
            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [marketingChats]);

    const handleChatResponse = async ({ text, value, media = [] } = {}) => {
        let tempprojectSessionId =
            selectedProjectChat?._id || isSelectedprojectSessionId?.current?._id;

        setTempId(tempprojectSessionId);

        const tempUserMessage = {
            role: "user",
            message: text || inputText,
            projectSessionId: tempprojectSessionId || `temp-user-${Date.now()}`,
            ...(media.length > 0 && { media: media.map((file) => file.fileUrl) }),
            doc: media.length > 0 ? `temp-user-${Date.now()}` : null,
        };

        dispatch(setProjectQueryMessage(tempUserMessage));
        dispatch(setProjectChatsessionId(selectedProjectChat?._id));

        setTimeout(() => {
            if (scrollRef.current) {
                scrollRef.current.scrollTop = 0;
            }
        }, 100);

        const textInput = text || inputText;

        if (!textInput.trim()) return;

        setInputText("");

        const userMessage = {
            role: "user",
            message: textInput,
            _id: `temp-user-${Date.now()}`,
            ...(media.length > 0 && { media: media.map((file) => file.fileUrl) }),
            doc: media.length > 0 ? `temp-user-${Date.now()}` : null,
            projectSessionId: tempprojectSessionId || `temp-user-${Date.now()}`,
        };

        // 🔑 Stable loading ID
        const loadingId = `loading-${Date.now()}`;

        const loadingMessage = {
            role: "assistant",
            message: "Processing your request...",
            _id: loadingId,
            type: "loading",
            progress: 0,
        };

        try {
            setLoading(true);

            setMarketingChats((prev) => [...prev, userMessage, loadingMessage]);

            trackEvent("DeepSearch_UserMessageSent", {
                message: textInput,
                projectSessionId: tempprojectSessionId,
            });

            const payload = {
                projectId,
                query: textInput,
            };

            const res = await marketingAgentRequest(payload);
            const { data } = res;

            const pollingId =
                data?.jobId || data?.request_id || data?._id;

            const pollInterval = setInterval(async () => {
                try {
                    const statusRes = await marketingAgentStatusCheck({
                        projectId,
                        requestId: pollingId,
                    });

                    const statusData = statusRes.data;

                    // ✅ SUCCESS
                    if (statusData?.status === "completed") {
                        clearInterval(pollInterval);

                        const finalMessage = {
                            role: "assistant",
                            message:
                                statusData?.responseData?.content ||
                                statusData?.response ||
                                statusData?.result ||
                                "Analysis completed.",
                            source: statusData?.responseData?.source,
                            _id: `bot-${Date.now()}`,
                            projectSessionId:
                                statusData?.projectSessionId || tempprojectSessionId,
                            isAudio: false,
                            isDownloadable: false,
                            fileDownloadLink: "",
                            annotations: statusData?.annotations || [],
                            visitedURLs: statusData?.visitedURLs || [],
                        };

                        setMarketingChats((prev) => [
                            ...prev.filter((c) => c._id !== loadingId),
                            finalMessage,
                        ]);

                        setDisplayedText(finalMessage.message);

                        trackEvent("MarketingAgent_ResponseReceived", {
                            response: finalMessage.message,
                            projectSessionId: finalMessage.projectSessionId,
                        });
                    }

                    // ❌ FAILED
                    else if (
                        statusData?.status === "failed" ||
                        statusData?.status === "error"
                    ) {
                        clearInterval(pollInterval);

                        const errorMessage = {
                            role: "assistant",
                            message:
                                statusData?.message ||
                                "Marketing analysis failed.",
                            _id: `error-${Date.now()}`,
                            statusCode: "error",
                        };

                        setMarketingChats((prev) => [
                            ...prev.filter((c) => c._id !== loadingId),
                            errorMessage,
                        ]);
                    }
                } catch (error) {
                    console.error("Polling error:", error);
                    trackException(error);

                    clearInterval(pollInterval);

                    // 🧹 Remove stuck loader on polling crash
                    setMarketingChats((prev) =>
                        prev.filter((c) => c._id !== loadingId)
                    );
                }
            }, 15000);
        } catch (error) {
            console.error("Request error:", error);
            trackException(error);

            const errorBotMessage = {
                role: "assistant",
                message: error?.message || "Something went wrong!!",
                _id: `error-bot-${Date.now()}`,
                statusCode: "error",
            };

            setMarketingChats((prev) => [
                ...prev.filter((c) => c._id !== loadingId),
                errorBotMessage,
            ]);
        } finally {
            setLoading(false);
        }
    };


    const isMicOn = useSelector((state) => state.voiceMode.isMicOn);
    const isMicOnRef = useRef(isMicOn);
    const isSelectedprojectSessionId = useRef(selectedProjectChat);

    useEffect(() => {
        isSelectedprojectSessionId.current = selectedProjectChat;
    }, [selectedProjectChat]);

    useEffect(() => {
        isMicOnRef.current = isMicOn;
    }, [isMicOn]);

    // Add scroll to bottom function
    const scrollToBottom = () => {
        if (scrollRef.current) {
            scrollRef.current.scrollTop = 0;
        }
    };

    return (
        <div
            className={`flex-1 flex flex-col overflow-scroll sm:overflow-hidden  ${isMobile && sidebarOpen ? "opacity-50" : ""
                }`}
        >
            {!isLoadingHistory && !selectedProjectChat?._id && marketingChats?.length === 0 && (
                <div className="min-h-full sm:h-full sm:min-h-[calc(100vh-3rem)] flex flex-col items-center justify-start sm:justify-center w-full px-4 sm:px-6 md:px-10 lg:px-12 pt-20 sm:pt-10 pb-10">
                    {/* Header / Project Info Area */}
                    <div className="flex flex-row w-full items-center justify-between md:max-w-[768px] 2xl:max-w-5xl">
                        <div className="flex items-center gap-4 ">
                            <Folder className="w-10 h-10 text-[#1B5C29]" />
                            <h1 className="text-4xl font-normal text-gray-800">{name}</h1>
                        </div>

                    </div>

                    {/* File preview cards - outside the input box */}
                    <div className="w-full md:max-w-[768px] 2xl:max-w-5xl mt-1">
                    </div>
                    {/* Main card */}
                    <div
                        className={`w-full md:max-w-[768px] 2xl:max-w-5xl ${false ? "mt-2" : "mt-2"
                            } overflow-hidden`}
                    >
                        {/* Input area */}
                        <div className="">
                            <TextInput
                                inputRef={inputRef}
                                inputText={inputText}
                                handleChatResponse={handleChatResponse}
                                setInputText={setInputText}
                                inputFocused={inputFocused}
                                setInputFocused={setInputFocused}
                                loading={loading}
                                disabled={loading && selectedProjectChat?._id === tempId}
                                marketingChats={marketingChats}
                                isContentRendering={isContentRendering}
                                hasDocument={hasDocument}
                                isMultipleFileUploadEnabled={true}
                                isFileUploadOptionVisible={false}
                                showPromptActions={false}
                            />
                        </div>


                        {/* Toggles row */}
                        <ProjectTogglesRow projectType={"marketing"} />


                    </div>

                    {/* project chat history */}
                    {/* <ProjectChatHistory /> */}
                </div>
            )}

            {isLoadingHistory ? (
                <SkeletonLoader />
            ) : marketingChats?.length > 0 ? (
                <div
                    id="scrollableDiv"
                    ref={scrollRef}
                    className="flex-1 py-4 !px-[30px] sm:px-[50px] md:px-[20px] lg:px-[100px] xl:!px-[220px] 2xl:!px-[220px] overflow-y-auto flex flex-col-reverse mt-[40px]"
                >
                    <button
                        onClick={scrollToBottom}
                        className="fixed bottom-36 right-7 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-all duration-200 z-20"
                    >
                        <ChevronDown className="w-5 h-5 text-gray-600" />
                    </button>
                    <InfiniteScroll
                        inverse={true} // Key for chat-style
                        dataLength={marketingChats.length}
                        next={fetchMoreData}
                        hasMore={hasMore}
                        scrollableTarget="scrollableDiv"
                        loader={<div className="mx-auto" key={0}></div>}
                        endMessage={
                            marketingChats?.totalPages >= 2 ? (
                                <p className="text-center m-2 font-segoeUI">No More marketingChats!</p>
                            ) : (
                                ""
                            )
                        }
                        scrollThreshold="200px"
                        className="overflow-hidden"
                    >
                        {marketingChats.map((chat, i) =>
                            chat.role === "user" ? (
                                <div
                                    key={chat._id || `chat-${i}`}
                                    className={`flex group justify-end ${i === 0 ? "mt-12 mb-8 " : "mt-8 mb-8"
                                        }`}
                                >
                                    <div className="flex flex-row gap-2">
                                        <div className="flex flex-col ">
                                            <div className="bg-[#1b5c2926] text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-[16px] rounded-br-[16px] rounded-tl-[16px] p-[14px] min-h-[44px] justify-center w-fit group">
                                                <div
                                                    className="markDownWrapper !leadings-[27px] font-hel leading-[27px] w-fit whitespace-pre-line break-words font-medium text-sm "
                                                >
                                                    <Markdown
                                                        key={`chat-${chat?._id}`}
                                                        options={{
                                                            overrides: {
                                                                a: { component: CustomLink },
                                                            },
                                                        }}
                                                    >
                                                        {preprocessText(chat.message)}
                                                    </Markdown>
                                                </div>
                                                {chat.media && chat.media.length > 0 && (
                                                    <div className="mt-2 flex flex-wrap gap-2">
                                                        {chat.media.map((mediaItem, fileIndex) => (
                                                            <FileCard
                                                                index={i}
                                                                key={`filecard-${mediaItem}`}
                                                                mediaItem={mediaItem}
                                                                fileIndex={fileIndex}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex items-center justify-between mt-1">
                                                <CopyToClipboard message={chat.message} id={chat._id} />
                                                {chat?.isAudio && (
                                                    <div className="p-1 ">
                                                        <Mic className="text-[#000000B2]" size={16} />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        {!isMobile && (
                                            <div className="!w-[35px] flex-shrink-0">
                                                <img
                                                    src={Avatar}
                                                    alt="Dabur Logo"
                                                    className="w-[40px]"
                                                />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ) : (
                                ((selectedProjectChat?._id && selectedProjectChat?._id === chat.projectSessionId) ||
                                    !selectedProjectChat?._id) && (
                                    <div key={chat._id} className="mt-8 mb-8 max-w-4xl space-y-8">
                                        <div className="flex gap-4 w-[280px] sm:w-full">
                                            {!isMobile && (
                                                <div className="w-[40px]">
                                                    <img
                                                        src={daburLogo}
                                                        alt="Dabur Logo"
                                                        className="w-[40px]"
                                                    />
                                                </div>
                                            )}
                                            <div className="text-[#000000] flex flex-col gap-2 overflow-x-auto rounded-bl-lg rounded-br-lg rounded-tr-lg min-h-[44px] justify-center w-fit group">
                                                {chat.type === "loading" ? (
                                                    <span className="italic text-gray-500">
                                                        {/* <WaveTextLoader text={chat.message} /> */}
                                                        <RotatingTextLoader messages={[
                                                             "Understanding your request",
                                                            "Searching relevant information",
                                                            "Analyzing context",
                                                            "Synthesizing details",
                                                            "Preparing response",
                                                            "Finalizing results"
                                                        ]} />
                                                    </span>
                                                ) : (
                                                    <div
                                                        className="markDownWrapper !leadings-[27px] font-hel w-fit whitespace-pre-line break-words font-medium text-md"
                                                    >
                                                        <DeepSearchBotMarkdown
                                                            content={deepSearchPreprocessText(
                                                                chat.message ?? displayedText,
                                                                chat?.annotations
                                                            )}
                                                            annotations={chat?.annotations || []}
                                                            visitedURLs={chat?.visitedURLs || []}
                                                        />

                                                        <CopyToClipboard
                                                            message={chat.message}
                                                            id={chat._id}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )
                            )
                        )}

                    </InfiniteScroll>
                </div>
            ) : selectedProjectChat?._id ? (
                <SkeletonLoader />
            ) : (
                <></>
            )}

            {/* Text Input and File Uploading */}
            {marketingChats?.length > 0 &&
                <div
                    className={classNames(
                        "sticky shadow-[0_-4px_20px_rgba(0,0,0,0.08)] bg-[#ECF2EE] bottom-0 mb-0 py-4 !px-[30px] sm:!px-[150px] md:!px-[20px] lg:!px-[200px] xl:!px-[200px] 2xl:!px-[200px]",
                        {
                            "opacity-50 pointer-events-none":
                                loading && selectedProjectChat?._id === tempId,
                        }
                    )}
                >
                    <div
                        className="relative w-full !h-[72px] rounded-[14px] !shadow-[0px_1px_2px_0px_rgba(16,24,40,0.05)]
        "
                    >
                        <TextInput
                            inputRef={inputRef}
                            inputText={inputText}
                            handleChatResponse={handleChatResponse}
                            setInputText={setInputText}
                            inputFocused={inputFocused}
                            setInputFocused={setInputFocused}
                            loading={loading}
                            disabled={loading && selectedProjectChat?._id === tempId}
                            marketingChats={marketingChats}
                            isContentRendering={isContentRendering}
                            hasDocument={hasDocument}
                            isMultipleFileUploadEnabled={true}
                            isFileUploadOptionVisible={false}
                            showPromptActions={false}
                        />
                    </div>
                    {/* Toggles row */}
                    <ProjectTogglesRow projectType={"marketing"} />
                    <div className="max-w-4xl mx-auto mt-[40px]">
                        <p className="text-xs text-gray-500 text-center">
                            {MESSAGES.POWERED_BY}
                        </p>
                    </div>
                </div>
            }
        </div>
    );
}

GenerateMarketing.propTypes = {
    isMobile: PropTypes.bool,
    sidebarOpen: PropTypes.bool,
    setMarketingChats: PropTypes.func,
    marketingChats: PropTypes.array,
};
